import { useCallback, useEffect, useRef, useState } from 'react';
import {
  mountCollabEditor,
  registerEmbeddableExtension,
  type CollabEditorCommentsOptions,
  type CollabEditorSource,
  type CollabEditorUser,
  type PlacedViewTypeSource,
  type TrackerReferenceResolver,
} from '@nimbalyst/collab-bundle/editor';
import {
  CollabDocumentSurface,
  type CollabDocumentPresentation,
  type CollabSurfaceHandle,
  type CollabSurfaceMount,
} from './CollabDocumentSurface';
import { createForwardingTrackerReferences } from './forwardingTrackerReferences';
import { loadPageHistory } from '@nimbalyst/collab-bundle/docs-ui';
import { WebDocumentHistoryDialog } from './WebDocumentHistoryDialog';
import { registerWebDocumentHistory, startWebDocumentHistoryRecording } from './webDocumentHistory';
import { useTeamDocumentEmbeds } from './DecisionArtifactPreview';
import { onWebDocumentTypesChanged, webEmbeddableSuffixes } from './webDocumentTypeCatalog';

export { BROWSER_DOCUMENT_STATE_LIMIT_BYTES } from './CollabDocumentSurface';

export interface CollabEditorMountProps {
  source: CollabEditorSource;
  user: CollabEditorUser;
  comments?: CollabEditorCommentsOptions;
  desktopHref?: string;
  /** `inline` drops the page chrome; see `CollabDocumentPresentation`. */
  presentation?: CollabDocumentPresentation;
  /**
   * Host-imposed read-only, combined with the server's answer by the session
   * rather than overriding it. A markdown canvas card is read-only until it is
   * activated; the document route passes nothing and is unchanged.
   */
  readOnly?: boolean;
  /** Development/test observer for the live collaborative surface. */
  onLexicalEditor?: Parameters<typeof mountCollabEditor>[0]['onLexicalEditor'];
  onHandleChange?: (handle: CollabSurfaceHandle | null) => void;
  /**
   * Embed renderers. A team-room page that passes neither renders shared
   * documents embedded in it live (`useTeamDocumentEmbeds`); an inline mount
   * (an embed or card itself) leaves them unavailable, so a page cannot embed
   * itself through another. Read at render, never a mount dependency.
   */
  renderDecisionArtifact?: Parameters<typeof mountCollabEditor>[0]['renderDecisionArtifact'];
  renderFileEmbed?: Parameters<typeof mountCollabEditor>[0]['renderFileEmbed'];
  /**
   * Live tracker references for the body. Passing the prop at all -- `null`
   * included, for a host whose tracker room is still opening -- opts the mount
   * in: the editor gets one stable forwarding resolver at mount, and a
   * resolver that arrives or changes later is swapped in underneath it rather
   * than remounting the live document. Omit it and references stay key-only.
   */
  trackerReferences?: TrackerReferenceResolver | null;
  /**
   * `'quiet'` renders inline references in prose as the item title with a
   * tinted underline and a hover peek, instead of the key-and-title chip.
   * Read when the editor mounts; presentation only.
   */
  trackerReferenceAppearance?: 'chip' | 'quiet';
  /** The typed page this body belongs to, so its links offer relations. Read at mount. */
  trackerReferenceSource?: { itemId: string; type: string };
  /** The project's types, for placing views from the slash menu. Read at mount. */
  placedViewTypes?: PlacedViewTypeSource;
  mountEditor?: typeof mountCollabEditor;
}

let embeddableSync: (() => void) | null = null;

/**
 * Declares the extension editors' suffixes to the editor once, and again when
 * the pins change, so a link to a drawing typed or pasted in the browser
 * becomes an embed as it does on the desktop.
 */
function syncWebEmbeddableSuffixes(): void {
  if (embeddableSync) return;
  embeddableSync = () => webEmbeddableSuffixes().forEach((suffix) => registerEmbeddableExtension(suffix));
  embeddableSync();
  onWebDocumentTypesChanged(embeddableSync);
}

/**
 * The Lexical editor over a shared markdown document.
 *
 * Everything that is not Lexical-specific — the connection state machine, the
 * browser size ceiling, the deferred teardown flush and the panels that explain
 * a document that will not open — lives in {@link CollabDocumentSurface}, which
 * an extension-provided editor mounts through as well.
 */
export function CollabEditorMount({
  source,
  user,
  comments,
  desktopHref,
  presentation,
  readOnly,
  onHandleChange: observeHandleChange,
  onLexicalEditor,
  renderDecisionArtifact,
  renderFileEmbed,
  trackerReferences,
  trackerReferenceAppearance,
  trackerReferenceSource,
  placedViewTypes,
  mountEditor = mountCollabEditor,
}: CollabEditorMountProps) {
  /**
   * The comment options the next mount will use, held off the effect's
   * dependencies.
   *
   * `mountCollabEditor` is imperative and the editor it builds is stateful, so
   * a new object identity here must never reach the mount's dependency array —
   * that would tear down a live editor and destroy its Y.Doc behind a flush,
   * which is exactly why `user` is excluded too. A caller that rebuilds this
   * object on an incidental render (a title that resolves, a name that arrives)
   * gets the same editor, and the mount reads whatever the ref holds when it
   * runs.
   */
  const lexicalRef = useRef(onLexicalEditor); lexicalRef.current = onLexicalEditor;
  const teamEmbeds = useTeamDocumentEmbeds(source, readOnly);
  const ownEmbeds = presentation !== 'inline' && !renderDecisionArtifact && !renderFileEmbed ? teamEmbeds : null;
  const artifactRef = useRef(renderDecisionArtifact);
  artifactRef.current = ownEmbeds?.renderDecisionArtifact ?? renderDecisionArtifact;
  const fileEmbedRef = useRef(renderFileEmbed);
  fileEmbedRef.current = ownEmbeds?.renderFileEmbed ?? renderFileEmbed;
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  /**
   * The live handle. Only the comment-access push below reads it, and it is
   * cleared by the surface the moment the editor is released.
   */
  const handleRef = useRef<ReturnType<typeof mountCollabEditor> | null>(null);
  /** Held off the mount dependencies, for the reason `comments` documents. */
  const readOnlyRef = useRef(readOnly);
  readOnlyRef.current = readOnly;
  const appearanceRef = useRef(trackerReferenceAppearance);
  appearanceRef.current = trackerReferenceAppearance;
  const referenceSourceRef = useRef(trackerReferenceSource);
  referenceSourceRef.current = trackerReferenceSource;
  const placedViewTypesRef = useRef(placedViewTypes);
  placedViewTypesRef.current = placedViewTypes;
  // Decided once: the editor reads its resolver only when it mounts.
  const [forwardedReferences] = useState(
    () => (trackerReferences === undefined ? null : createForwardingTrackerReferences()),
  );
  useEffect(() => {
    forwardedReferences?.setTarget(trackerReferences ?? null);
  }, [forwardedReferences, trackerReferences]);
  useEffect(() => () => forwardedReferences?.setTarget(null), [forwardedReferences]);

  const mount = useCallback<CollabSurfaceMount>((request) => {
    syncWebEmbeddableSuffixes();
    const handle = mountEditor({
      element: request.element,
      source: request.source,
      user: request.user,
      comments: commentsRef.current,
      renderDecisionArtifact: (entryId, artifact, embed) => artifactRef.current?.(entryId, artifact, embed),
      renderFileEmbed: (embed) => fileEmbedRef.current?.(embed) ?? null,
      readOnly: readOnlyRef.current,
      ...(forwardedReferences ? { trackerReferences: forwardedReferences } : {}),
      ...(appearanceRef.current ? { trackerReferenceAppearance: appearanceRef.current } : {}),
      ...(referenceSourceRef.current ? { trackerReferenceSource: referenceSourceRef.current } : {}),
      ...(placedViewTypesRef.current ? { placedViewTypes: placedViewTypesRef.current } : {}),
      onLexicalEditor: editor => lexicalRef.current?.(editor),
      onStateChange: request.onStateChange,
      onPresenceChange: request.onPresenceChange,
      onWriteRejected: request.onWriteRejected,
      onTermination: request.onTermination,
      onError: request.onError,
      onBindingError: request.onBindingError,
    });
    handleRef.current = handle;
    return handle;
  }, [forwardedReferences, mountEditor]);

  /**
   * Page history, for a page shown as a page (not an inline card, which would
   * publish a second controller for the same document). The dialog opens only
   * when something asks for this document's history, so a typed page body or
   * wiki body that nothing asks about shows nothing. While mounted, the page
   * also records the revisions that history is made of.
   */
  const historyDocumentId = source.kind === 'team-room' && presentation !== 'inline' ? source.room.documentId : null;
  const [historyHandle, setHistoryHandle] = useState<ReturnType<typeof mountCollabEditor> | null>(null);
  useEffect(() => {
    if (!historyDocumentId || !historyHandle || source.kind !== 'team-room') return;
    const page = { source, handle: historyHandle };
    const unregister = registerWebDocumentHistory(historyDocumentId, page);
    const stopRecording = startWebDocumentHistoryRecording(page, loadPageHistory);
    return () => {
      stopRecording();
      unregister();
    };
  }, [historyDocumentId, historyHandle, source]);

  const onHandleChange = useCallback((handle: CollabSurfaceHandle | null) => {
    if (!handle) handleRef.current = null;
    setHistoryHandle(handle ? handleRef.current : null);
    observeHandleChange?.(handle);
  }, [observeHandleChange]);

  /**
   * Carry a comment-access answer that resolved after the mount into the live
   * editor.
   *
   * The role comes from the REST roster, which lands well after the editor is
   * mounted on a cold open, and the bundle renders into its own React root — a
   * changed answer reaches nothing on its own, so the affordance would stay
   * withheld for the rest of the session. This must not be a mount dependency:
   * remounting for it would destroy a live Y.Doc behind a flush, which is why
   * `comments` is read through a ref above.
   */
  const canComment = comments?.canComment?.() ?? true;
  useEffect(() => {
    handleRef.current?.refreshCommentAccess();
  }, [canComment]);

  /** The same push, for host read-only. Skipped when the caller passed none. */
  useEffect(() => {
    if (readOnly === undefined) return;
    handleRef.current?.setReadOnly(readOnly);
  }, [readOnly]);

  return (
    <>
      <CollabDocumentSurface
        source={source}
        user={user}
        desktopHref={desktopHref}
        mount={mount}
        presentation={presentation}
        onHandleChange={onHandleChange}
      />
      {historyDocumentId ? <WebDocumentHistoryDialog documentId={historyDocumentId} /> : null}
    </>
  );
}
